import ArrowBack from '@mui/icons-material/ArrowBack';
import { Box, Grid, Stack } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { AddressDataType, StructureAddressProps } from './StructureAddress.props';
import { ColorProps } from '../../../@types/common.type';
import { GRID_SPACING, RESPONSIVE_FIELD_GRID_SX } from '../../../configs/ui-constants';
import { useGetCountries, useGetStatesByCountryId } from '../../../queries/address-query';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { Checkbox, CheckboxColorProps } from '../../atoms/Checkbox';
import { FormControlLabel, FormControlLabelPlacementProps } from '../../atoms/FormControlLabel';
import { SelectMenu } from '../../atoms/SelectMenu';
import { Switch } from '../../atoms/Switch';
import { TextField } from '../../atoms/TextField';
import { ManualAddressData, OptionType } from '../../molecules/ManualAddress';
import { ManualAddress } from '../../molecules/ManualAddress/ManualAddress';
import { MapSearch } from '../../molecules/MapSearch';

export const StructureAddress: React.FC<StructureAddressProps> = ({
  switchLabel,
  addressData,
  onChange,
  addressTypeData,
  disableTypeSelection = false,
  enableContactNumber,
  isService,
  enableAddressContactFields,
  isOneAddress,
  isEditMode,
  hasAddressEditPermission = true,
  isClientCustomerStructureCreation = false,
  onUseLocationName,
  error,
}) => {
  const [isActive, setIsActive] = useState(addressData ? addressData.isManual : false);
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [states, setStates] = useState<OptionType[]>([]);
  const [countryId, setCountryId] = useState<number>(0);

  const getStatesByCountryIdQuery = useGetStatesByCountryId(countryId);
  const getCountriesQuery = useGetCountries();

  useEffect(() => {
    getCountriesQuery.data && setCountries(getCountriesQuery.data);
  }, [getCountriesQuery.data]);

  useEffect(() => {
    getStatesByCountryIdQuery.data && setStates(getStatesByCountryIdQuery.data);
  }, [getStatesByCountryIdQuery.data]);

  useEffect(() => {
    countries &&
      addressData?.countryName &&
      setCountryId(countries?.find((item) => item.name === addressData.countryName)?.id ?? 0);
  }, [addressData?.countryName, countries]);

  // Keyed on the watched value alone. Every consumer re-creates `onChange`
  // each render and each of these effects calls it, so listing it would loop.
  useEffect(() => {
    states &&
      addressData?.stateName &&
      onChange({
        ...addressData,
        stateId: states?.find((item) => item.name === addressData.stateName)?.id ?? 0,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData?.stateName, states]);

  useEffect(() => {
    countryId !== 0 && getStatesByCountryIdQuery.refetch();
    addressData &&
      onChange({
        ...addressData,
        countryId: countryId,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  useEffect(() => {
    if (isOneAddress && (isEditMode || isClientCustomerStructureCreation)) {
      setIsActive(addressData?.isManual);
      addressData &&
        onChange({
          ...addressData,
          isManual: addressData?.isManual,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData?.isManual]);

  const handleIsActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
    addressData &&
      onChange({
        ...addressData,
        isManual: event.target.checked,
      });
  };

  // The switch chooses how the address is entered, so it reads as a heading for
  // the fields rather than a control parked alongside them. Beside the form it
  // also claimed a whole column: in the half-width Address panel that squeezed
  // twelve fields into a single 150px-wide column of truncated labels.
  const modeControls = (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
      <FormControlLabel
        control={
          <Switch
            disabled={!hasAddressEditPermission}
            // Primary, not Success: this sits on the same page as the Active
            // toggle and the capability switches, which are all primary. A lone
            // green switch reads as a different kind of control.
            color={ColorProps.Primary}
            onChange={handleIsActiveChange}
            checked={addressData?.isManual}
          />
        }
        label={switchLabel}
      />
      {isService && (
        <FormControlLabel
          value="start"
          control={
            <Checkbox
              disabled={!hasAddressEditPermission}
              checked={addressData?.isKeep}
              onChange={() =>
                addressData && onChange({ ...addressData, isKeep: !addressData.isKeep })
              }
              color={CheckboxColorProps.Success}
            />
          }
          label="Keep"
          labelPlacement={FormControlLabelPlacementProps.Start}
        />
      )}
    </Stack>
  );

  return (
    <Stack mb={1} spacing={1}>
      {modeControls}
      <Grid container spacing={GRID_SPACING} width="100%">
        {isActive ? (
          <Grid size={{ xs: 12 }}>
            <ManualAddress
              manualAddressData={addressData as ManualAddressData}
              onChange={(data) => onChange(data as AddressDataType)}
              addressTypeData={addressTypeData}
              disableTypeSelection={disableTypeSelection}
              countries={countries}
              states={states}
              enableContactNumber={enableContactNumber}
              enableAddressContactFields={enableAddressContactFields}
              hasAddressEditPermission={hasAddressEditPermission}
              isClientCustomerStructureCreation={isClientCustomerStructureCreation}
              error={!!error}
            />
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: 12, sm: isService ? 12 : 9 }}>
              <MapSearch
                disabled={!hasAddressEditPermission}
                label="Google Address"
                addressData={addressData as AddressDataType}
                selectedValue={addressData?.googleAddress}
                onChange={(data) => onChange(data as AddressDataType)}
                hasError={!!error && !addressData?.googleAddress}
                required={isClientCustomerStructureCreation}
              />
            </Grid>
            {!isService && (
              <Grid size={{ xs: 12, sm: 3 }}>
                <SelectMenu
                  label="Type"
                  id="address-type"
                  items={addressTypeData}
                  disabled={disableTypeSelection || !hasAddressEditPermission}
                  selectedValue={addressData?.addressType}
                  onChange={(text) => {
                    onChange({
                      ...addressData,
                      addressType: text,
                    });
                  }}
                />
              </Grid>
            )}
            {enableAddressContactFields && (
              <Grid size={{ xs: 12 }} sx={{ containerType: 'inline-size' }}>
                <Box sx={RESPONSIVE_FIELD_GRID_SX}>
                  <Box>
                    <TextField
                      fullWidth
                      disabled={!hasAddressEditPermission}
                      label="Contact Name"
                      value={addressData?.name}
                      onChange={(text) => {
                        addressData &&
                          onChange({
                            ...addressData,
                            name: text,
                          });
                      }}
                      required={isClientCustomerStructureCreation}
                      error={!!error && !addressData?.name}
                    />
                  </Box>
                  <Box>
                    <TextField
                      fullWidth
                      disabled={!hasAddressEditPermission}
                      label="Contact Number"
                      value={addressData?.contactNumber}
                      onChange={(text) => {
                        addressData &&
                          onChange({
                            ...addressData,
                            contactNumber: text,
                          });
                      }}
                      required={isClientCustomerStructureCreation}
                      error={!!error && !addressData?.contactNumber}
                    />
                  </Box>
                  {isClientCustomerStructureCreation && (
                    <Box>
                      <TextField
                        fullWidth
                        disabled={!hasAddressEditPermission}
                        label="Email"
                        value={addressData?.email}
                        onChange={(text) => {
                          addressData &&
                            onChange({
                              ...addressData,
                              email: text,
                            });
                        }}
                      />
                    </Box>
                  )}
                  <Box>
                    <TextField
                      fullWidth
                      disabled={!hasAddressEditPermission}
                      label="Contact Company"
                      value={addressData?.contactCompany}
                      onChange={(text) => {
                        addressData &&
                          onChange({
                            ...addressData,
                            contactCompany: text,
                          });
                      }}
                      required={isClientCustomerStructureCreation}
                      InputLabelProps={{
                        shrink: !!addressData?.contactCompany,
                      }}
                      error={!!error && !addressData?.contactCompany}
                    />
                  </Box>
                  {isClientCustomerStructureCreation && (
                    <Box sx={{ alignSelf: 'center' }}>
                      <Button
                        variant={ButtonVariantProps.Secondary}
                        startIcon={<ArrowBack />}
                        onClick={() => onUseLocationName && onUseLocationName()}
                      >
                        Use Location Name
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Stack>
  );
};
